import { sessionStore } from '@/lib/session';
import { safeAnalyticsPath } from '@/lib/analyticsModel';

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const QUEUE_KEY = 'carrfields-analytics-queue-v1';
const VISITOR_KEY = 'carrfields-analytics-visitor-v1';
const SESSION_KEY = 'carrfields-analytics-session-v1';
type EventProperties = { listingId?: string; category?: string; queryLength?: number; seconds?: number };
interface AnalyticsEvent { id: string; type: string; occurredAt: number; visitorId: string; sessionId: string; path: string; displayMode: string; platform: string; properties: EventProperties }
interface QueuedEvent { userId: string | null; event: AnalyticsEvent }
let visitorId = '', session: { id: string; lastActivity: number; userId: string | null } | null = null;
let queue: QueuedEvent[] = [], loaded = false, sending = false;
export function analyticsDisplayMode() {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true ? 'standalone' : 'browser';
}
export function analyticsPlatform() {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'iOS';
  if (/Android/.test(navigator.userAgent)) return 'Android';
  return 'Desktop';
}
const currentUserId = () => { try { return sessionStore.getUser()?.userId || null; } catch { return null; } };
function load() {
  if (loaded) return; loaded = true;
  try { visitorId = localStorage.getItem(VISITOR_KEY) || ''; queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); session = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { queue = []; }
  if (!/^[a-zA-Z0-9_-]{1,100}$/.test(visitorId)) visitorId = crypto.randomUUID();
  if (!Array.isArray(queue)) queue = [];
  queue = queue.filter((q) => q?.event?.occurredAt > Date.now() - 7 * 86400000 && q.event.visitorId === visitorId).slice(-200);
  try { localStorage.setItem(VISITOR_KEY, visitorId); } catch { /* Tracking remains best effort. */ }
}
function persist() { try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)); } catch { /* Memory queue remains available. */ } }
function context() {
  load(); const now = Date.now(), userId = currentUserId();
  const isNew = !session || !session.id || now - session.lastActivity >= 30 * 60000 || (session.userId !== null && session.userId !== userId);
  if (isNew) session = { id: crypto.randomUUID(), lastActivity: now, userId };
  else session = { ...session!, lastActivity: now, userId };
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* Use this tab's in-memory session. */ }
  return { visitorId, sessionId: session.id, userId, isNew };
}
function append(type: string, properties: EventProperties, c: ReturnType<typeof context>) {
  queue.push({ userId: c.userId, event: { id: crypto.randomUUID(), type, occurredAt: Date.now(), visitorId: c.visitorId, sessionId: c.sessionId, path: safeAnalyticsPath(location.pathname), displayMode: analyticsDisplayMode(), platform: analyticsPlatform(), properties } });
  queue = queue.slice(-200); persist();
}
export function trackActivity(type: string, properties: EventProperties = {}) {
  if (!API_URL) return;
  const c = context(); if (c.isNew) append('session_start', {}, c);
  append(type, properties, c);
}
export function analyticsRequestHeaders(): Record<string, string> {
  load();
  // Polling must not prolong sessions or manufacture activity while the app is idle.
  return { 'X-Analytics-Visitor': visitorId, 'X-Analytics-Session': session?.id || `prelaunch-${visitorId}`, 'X-Analytics-Display-Mode': analyticsDisplayMode(), 'X-Analytics-Platform': analyticsPlatform() };
}
export async function flushAnalytics(keepalive = false): Promise<void> {
  load(); if (sending || !queue.length || !navigator.onLine || !API_URL) return;
  sending = true;
  try {
    // Old account events are retained until that account returns, never assigned to another login.
    const userId = currentUserId();
    const eligible = queue.filter((q) => q.userId === userId || q.userId === null);
    const batchIdentity = eligible.find((q) => q.userId === userId)?.userId ?? eligible[0]?.userId;
    const batch = eligible.filter((q) => q.userId === batchIdentity).slice(0, 20);
    if (!batch.length) return;
    const token = batchIdentity ? sessionStore.getToken() : null;
    if (batchIdentity && !token) return;
    const response = await fetch(`${API_URL}/analytics/events`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ events: batch.map((q) => q.event) }), keepalive, ...(!keepalive ? { signal: AbortSignal.timeout(5000) } : {}) });
    if (response.ok || response.status === 400) {
      const ids = new Set(batch.map((q) => q.event.id)); queue = queue.filter((q) => !ids.has(q.event.id)); persist();
    }
  } catch { /* Offline/transient failures must never interrupt the app. */ }
  finally { sending = false; }
}
export interface AnalyticsUser { userId: string; name: string; email: string; role: string; isTest: boolean; sessions: number; daysActive: number; pageViews: number; listingViews: number; engagedSeconds: number; actions: number; enquiries: number; lastSeen: number | null; installed: boolean }
export interface ActivityEvent extends Omit<AnalyticsEvent, 'id'> { eventId: string; userId: string | null; role: string; source: string; isTest: boolean }
export interface AnalyticsSummary {
  from: string; to: string; timeZone: string; retentionDays: number; truncated: boolean;
  totals: { events: number; activeUsers: number; sessions: number; visitors: number; pageViews: number; listingViews: number; engagedSeconds: number; browserReportedInstalls: number; observedInstalledDevices: number; observedInstalledUsers: number; enquiries: number };
  eventCounts: Record<string, number>; daily: { day: string; pageViews: number; sessions: number; activeUsers: number; engagedSeconds: number }[]; users: AnalyticsUser[];
}
async function analyticsGet<T>(path: string, params: Record<string, string>): Promise<T> {
  const response = await fetch(`${API_URL}${path}?${new URLSearchParams(params)}`, { headers: { Authorization: `Bearer ${sessionStore.getToken() || ''}` } });
  const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Unable to load analytics'); return data;
}
export const fetchAnalyticsSummary = (params: Record<string, string>) => analyticsGet<AnalyticsSummary>('/analytics/summary', params);
export const fetchUserActivity = (id: string, params: Record<string, string>) => analyticsGet<{ events: ActivityEvent[]; cursor: string | null }>(`/analytics/users/${encodeURIComponent(id)}/events`, params);
